import React, { useEffect, useRef } from 'react';

import type { ResponseError } from '@rtkwlf/insights';
import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router-dom';

type SentryErrorBoundaryProps = React.ComponentProps<
  typeof Sentry.ErrorBoundary
>;

export type InsightsBaseErrorBoundaryProps = React.PropsWithChildren<
  Omit<SentryErrorBoundaryProps, 'fallback'> & {
    fallback: React.ReactElement;
    notFoundFallback?: React.ReactElement;
  }
>;

function isResponseError(error: unknown): error is ResponseError {
  return (error as ResponseError).name === 'ResponseError';
}

export const InsightsBaseErrorBoundary = React.memo(
  (props: InsightsBaseErrorBoundaryProps) => {
    const { fallback, beforeCapture, notFoundFallback, ...rest } = props;
    const location = useLocation();
    const resetErrorRef = useRef<(() => void) | null>(null);

    // if the the component is in a fallback state it will have an instance of the resetError callback
    // and this will be called when the location changes
    useEffect(() => {
      resetErrorRef.current?.();
    }, [location.key, resetErrorRef]);

    return (
      <Sentry.ErrorBoundary
        fallback={({ resetError, error }) => {
          resetErrorRef.current = resetError;
          if (
            notFoundFallback &&
            isResponseError(error) &&
            error.response.status === 404
          ) {
            return notFoundFallback;
          }
          return fallback;
        }}
        beforeCapture={(scope, error, componentStack) => {
          scope.setTag('app', 'insights');
          beforeCapture?.(scope, error, componentStack);
        }}
        {...rest}
      />
    );
  },
);
